import /* postSaveData */

/* , saveDataApi */
'@api/data';

import {
    Button,
    Icon,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import BigSectionTitleComponent from '@components/BigSectionTitleComponent/BigSectionTitleComponent';
import CustomerCustomFieldElement from '@components/CustomerCustomFieldElement/CustomerCustomFieldElement';
import LoaderModal from '@components/LoaderModal/LoaderModal';
import SmallSectionTitleComponent from '@components/SmallSectionTitleComponent/SmallSectionTitleComponent';
import { AppRoutes } from '@routes/appRoutes';
import palette from '@theme/assets/palette';
import { fileToBase64 } from '@utils/functions';
import { FC, createRef, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MdDownload } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { CustomFieldWithId, CustomerCustomField, CustomerForm, FileData } from 'types/formData';
import { StoreContextType } from 'types/reducers';
import { StoreContext } from '@contexts/StoreContext';
import { postSaveData } from '@api/data';
import ReCAPTCHA from 'react-google-recaptcha';

interface Props {
    customerForm: CustomerForm;
    endForm: (newForm: CustomerForm) => void;
    previousStep: () => void;
}

const Step3: FC<Props> = ({ customerForm, previousStep }) => {
    const { state } = useContext<StoreContextType>(StoreContext);
    const recaptchaRef = createRef<ReCAPTCHA>();
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isLoading, setIsLoading] = useState(false);
    const [formDataCustomer, setFormDataCustomer] = useState<CustomerForm>(customerForm);
    const {
        handleSubmit,
        register,
        control,
        formState: { errors, isSubmitting, isValid },
    } = useForm({ mode: 'onBlur' });

    const isPa = useMemo(() => {
        return formDataCustomer.pa ?? false;
    }, [formDataCustomer.pa]);

    useEffect(() => {
        setFormDataCustomer(customerForm);
    }, [customerForm]);

    const onSubmit = useCallback(() => {
        if (isValid && recaptchaRef && recaptchaRef.current) {
            setIsLoading(true);
            const cleanedFormDataCustomer = {
                ...formDataCustomer,
                customFields: formDataCustomer.customFields.filter((field) => !field.collapsed),
            };
            delete cleanedFormDataCustomer.validations;
            postSaveData(cleanedFormDataCustomer, recaptchaRef.current)
                .then(() => {
                    setIsLoading(false);
                    onOpen();
                })
                .catch((error) => {
                    setIsLoading(false);
                    console.log(error);
                });
        }
        return;
    }, [formDataCustomer, isValid, onOpen, recaptchaRef]);

    const handleUpdateField = useCallback(
        (newCustomField: CustomerCustomField, index: number) => {
            const newArray: CustomerCustomField[] = formDataCustomer.customFields.map((customField, i) => {
                if (i === index) {
                    return newCustomField;
                } else {
                    return customField;
                }
            });
            setFormDataCustomer({ ...formDataCustomer, customFields: newArray });
        },
        [formDataCustomer],
    );

    const customFieldsArray: CustomFieldWithId[] = useMemo(() => {
        return formDataCustomer.customFields.map((field, index) => {
            return { ...field, id: `defaultCustomField_${index}` };
        });
        // const arrayFields: CustomFieldWithId[] = [];
        // const fillNameCustomFields = formDataCustomer.customFields.filter((field) => field.name);
        // fillNameCustomFields.forEach((field, index) =>
        //     arrayFields.push({ ...field, id: `defaultCustomField_${index}` }),
        // );
        // const voidNameCustomFields = formDataCustomer.customFields.filter((field) => !field.name);
        // voidNameCustomFields.forEach((field, index) => arrayFields.push({ ...field, id: `addedCustomField_${index}` }));
        // return arrayFields;
    }, [formDataCustomer]);

    const eraseCustomField = useCallback(
        (position: number) => {
            setFormDataCustomer({
                ...formDataCustomer,
                customFields: formDataCustomer.customFields.map((field) => {
                    if (position === field.position) {
                        return {
                            ...field,
                            collapsed: true,
                        };
                    }
                    return field;
                }),
            });
        },
        [formDataCustomer],
    );

    const addCustomField = useCallback(
        (position: number) => {
            setFormDataCustomer({
                ...formDataCustomer,
                customFields: formDataCustomer.customFields.map((field) => {
                    if (position === field.position) {
                        return {
                            ...field,
                            collapsed: false,
                        };
                    }
                    return field;
                }),
            });
        },
        [formDataCustomer],
    );

    const uploadFile = useCallback(
        async (position: number, fieldName: string, files: File[]) => {
            if (files) {
                const typeId = state.attachmentTypes.find((at) => at.tag === 'PICK_LIST')?.id ?? 0;
                const attachments: FileData[] = [];
                for (let i = 0; i < files.length; i++) {
                    const filestream = (await fileToBase64(files[0])) as string;
                    const name = files[0].name;
                    const size = files[0].size;
                    const mimeType = files[0].type;
                    attachments.push({ filestream: filestream.split(',')[1], name, size, mimeType, typeId });
                }
                setFormDataCustomer({
                    ...formDataCustomer,
                    customFields: formDataCustomer.customFields.map((field) => {
                        if (position === field.position) {
                            if (position !== 0) {
                                return {
                                    ...field,
                                    attachments,
                                };
                            } else {
                                if (field.name === fieldName) {
                                    return {
                                        ...field,
                                        attachments,
                                    };
                                } else {
                                    return field;
                                }
                            }
                        }
                        return field;
                    }),
                });
            }
        },
        [formDataCustomer],
    );

    const deleteFile = useCallback((position: number) => {
        setFormDataCustomer({
            ...formDataCustomer,
            customFields: formDataCustomer.customFields.map((field) => {
                if (position === field.position) {
                    return {
                        ...field,
                        attachments: null,
                    };
                }
                return field;
            }),
        });
    }, []);

    const customFieldsList: JSX.Element[] | null = useMemo(() => {
        if (formDataCustomer) {
            const arrayFields: JSX.Element[] = customFieldsArray.map((customField, index) => {
                return (
                    <CustomerCustomFieldElement
                        customField={customField}
                        register={register}
                        control={control}
                        errors={errors}
                        customIndex={customField.position}
                        key={customField.id}
                        id={customField.id}
                        isErasable={customField.position != 0 && !customField.collapsed}
                        onEraseField={() => eraseCustomField(customField.position)}
                        onAddField={() => addCustomField(customField.position)}
                        onUploadFile={(files: File[]) => uploadFile(customField.position, customField.name, files)}
                        onDeleteFile={() => deleteFile(customField.position)}
                        isPa={formDataCustomer.pa}
                        tooltipText={
                            index > 1
                                ? 'In questi campi è possibile inserire informazioni legate alla singola transazione (es. divisione, centro di costo alternativo, sede)'
                                : undefined
                        }
                        updateCustomField={(newCustomField: CustomerCustomField) =>
                            handleUpdateField(newCustomField, index)
                        }
                    />
                );
            });
            return arrayFields;
        }
        return null;
    }, [
        formDataCustomer,
        customFieldsArray,
        register,
        errors,
        Object.keys(errors),
        handleUpdateField,
        eraseCustomField,
    ]);

    // const handleAddNewCustomField = useCallback(() => {
    //     const newCustomField = {
    //         ...voidCustomField,
    //         position:
    //             !isPa && formDataCustomer.customFields.length > 4
    //                 ? formDataCustomer.customFields.length + 1
    //                 : formDataCustomer.customFields.length - 1,
    //     };
    //     setFormDataCustomer({
    //         ...formDataCustomer,
    //         customFields: [...formDataCustomer.customFields, newCustomField],
    //     });
    // }, [formDataCustomer, isPa]);

    const disabledAddCustomField = useMemo(() => {
        const filteredVoidArray = formDataCustomer.customFields.filter((field) => !field.name && !field.customName);
        if (
            filteredVoidArray.length > 0 ||
            (isPa && formDataCustomer.customFields.length > 11) ||
            (!isPa && formDataCustomer.customFields.length > 9)
        ) {
            return true;
        }
        return false;
    }, [formDataCustomer.customFields, isPa]);

    return (
        <form>
            <Stack
                paddingX="20px"
                paddingTop="20px"
                paddingBottom="60px"
                justify="center"
                align="center"
                spacing="20px"
                width="900px"
                maxWidth="100%"
                margin="0 auto"
            >
                <BigSectionTitleComponent title="Campi personalizzati" />
                <SmallSectionTitleComponent title="Indicare i campi che si desidera vengano gestiti dall’agenzia" />
                <Text variant="subtitle">I campi fissi verranno importati dal profilo</Text>
                <Text variant="subtitle" opacity={0.6}>
                    * con “significativo” verranno rendicontati i movimenti di storno e riaddebito delle variazioni
                    apportate al campo.{' '}
                </Text>
                <Text variant="subtitle" opacity={0.6}>
                    ** se presente una Pick-List precisare i valori associati alla tabella da caricare{' '}
                </Text>
                {customFieldsList}
                <Stack
                    direction="row"
                    justify="center"
                    align="center"
                    spacing="10px"
                    alignSelf="stretch"
                    padding="20px 0"
                    marginBottom="60px"
                >
                    <Button
                        size="lg"
                        variant={'outline'}
                        borderColor={palette.brand01}
                        color={palette.brand01}
                        onClick={previousStep}
                    >
                        Indietro
                    </Button>
                    <Button
                        rightIcon={<Icon as={MdDownload} />}
                        size="lg"
                        variant={'main'}
                        bg={palette.brand01}
                        onClick={onSubmit}
                        isLoading={isSubmitting}
                        isDisabled={!isValid}
                    >
                        Invia
                    </Button>
                </Stack>
            </Stack>
            <LoaderModal isVisible={isLoading} />
            <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose} motionPreset="slideInBottom">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <SmallSectionTitleComponent title="Esito salvataggio" />
                    </ModalHeader>
                    <ModalBody>
                        <Text variant="subtitle">I dati del profilo sono stati salvati correttamente.</Text>
                    </ModalBody>

                    <ModalFooter justifyContent="center">
                        <Button
                            bg={palette.brand01}
                            variant={'main'}
                            onClick={() => {
                                onClose();
                                navigate(AppRoutes.HOME_PAGE);
                            }}
                        >
                            Ok
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <ReCAPTCHA
                size="invisible"
                theme="dark"
                sitekey={window.GattinoniAnagraphicForm.RECAPTCHA_PUBLIC_KEY}
                ref={recaptchaRef}
            />
        </form>
    );
};

export default Step3;
